"use client";

import { useMemo } from "react";
import { useRouter } from "next/navigation";
import {
  filterPaletteGroups,
  flattenGroupItems,
  type PaletteGroup,
  type PaletteItem,
  type PaletteWorker,
} from "@/lib/commandPalette";
import type { PaletteViewMode } from "./usePaletteView";

/** The groups the current screen lists, filtered by the query, and the same items flat. */
export function usePaletteItems({
  workers,
  search,
  viewMode,
  setOpen,
  setViewMode,
  setSelectedIndex,
  setSearch,
  handleToggleAllWorkers,
  handleTriggerWorker,
}: {
  workers: PaletteWorker[];
  search: string;
  viewMode: PaletteViewMode;
  setOpen: (open: boolean) => void;
  setViewMode: (mode: PaletteViewMode) => void;
  setSelectedIndex: (index: number) => void;
  setSearch: (value: string) => void;
  handleToggleAllWorkers: () => Promise<void>;
  handleTriggerWorker: (worker: PaletteWorker) => Promise<void>;
}) {
  const router = useRouter();

  // Construct Root Palette Groups
  const rawGroups: PaletteGroup[] = useMemo(() => {
    const navItems: PaletteItem[] = [
      {
        id: "nav-monitoring",
        type: "navigation",
        title: "Go to Monitoring",
        subtitle: "View company dashboards, metrics & reports",
        keywords: ["analytics", "overview", "metrics", "reports"],
        perform: () => {
          setOpen(false);
          router.push("/monitoring");
        },
      },
      {
        id: "nav-settings",
        type: "navigation",
        title: "Go to Settings",
        subtitle: "Manage provider keys, team members & configurations",
        keywords: ["config", "api keys", "providers", "team", "organization"],
        perform: () => {
          setOpen(false);
          router.push("/settings");
        },
      },
      {
        id: "nav-kb",
        type: "navigation",
        title: "Go to Knowledge Base",
        subtitle: "Explore company wiki & ingested documentation",
        keywords: ["kb", "wiki", "docs", "documentation", "sources"],
        perform: () => {
          setOpen(false);
          router.push("/knowledge");
        },
      },
      {
        id: "nav-inbox",
        type: "navigation",
        title: "Monitoring: Inbox",
        subtitle: "Review decisions waiting on you, approvals & errors",
        keywords: ["inbox", "waiting on you", "attention", "approvals", "decision"],
        perform: () => {
          setOpen(false);
          router.push("/monitoring/inbox");
        },
      },
      {
        id: "nav-create-worker",
        type: "navigation",
        title: "Create New Worker",
        subtitle: "Set up a new AI business agent",
        keywords: ["new", "create", "worker", "agent", "bot"],
        perform: () => {
          setOpen(false);
          router.push("/workers/new");
        },
      },
    ];

    const workerItems: PaletteItem[] = workers.map((w) => ({
      id: `worker-${w.id}`,
      type: "worker",
      title: w.name,
      subtitle: w.model,
      badge: {
        text: w.enabled ? "Active" : "Paused",
        variant: w.enabled ? "success" : "attention",
      },
      keywords: [w.name, w.model, w.enabled ? "active" : "paused", "worker", "agent"],
      perform: () => {
        setOpen(false);
        router.push(`/workers/${w.id}`);
      },
    }));

    const anyPaused = workers.some((w) => !w.enabled);
    const actionItems: PaletteItem[] = [
      {
        id: "action-run-worker",
        type: "action",
        title: "Run Worker...",
        subtitle: "Trigger a manual execution for a chosen worker",
        keywords: ["run", "execute", "trigger", "start", "now"],
        perform: () => {
          setViewMode("run-worker");
          setSelectedIndex(0);
          setSearch("");
        },
      },
      {
        id: "action-search-kb",
        type: "action",
        title: "Search Knowledge Base...",
        subtitle: "Jump directly to KB page search",
        keywords: ["kb", "knowledge", "search", "documents", "wiki", "find"],
        perform: () => {
          setViewMode("kb-search");
          setSelectedIndex(0);
        },
      },
      {
        id: "action-toggle-all",
        type: "action",
        title: anyPaused ? "Toggle All Workers (Resume)" : "Toggle All Workers (Pause)",
        subtitle: anyPaused
          ? "Resume all paused workers in the business"
          : "Pause all active workers in the business",
        badge: {
          text: anyPaused ? "Resume" : "Pause",
          variant: anyPaused ? "success" : "attention",
        },
        keywords: ["pause", "resume", "toggle", "all", "workers", "bulk"],
        perform: () => void handleToggleAllWorkers(),
      },
    ];

    return [
      { id: "navigation", label: "Navigation", items: navItems },
      { id: "workers", label: "Workers", items: workerItems },
      { id: "actions", label: "Quick Actions", items: actionItems },
    ];
  }, [workers, router, setOpen, handleToggleAllWorkers, setViewMode, setSelectedIndex, setSearch]);

  // Construct filtered groups for run-worker view
  const runWorkerGroups: PaletteGroup[] = useMemo(() => {
    return [
      {
        id: "select-worker",
        label: "Select Worker to Run",
        items: workers.map((w) => ({
          id: `run-${w.id}`,
          type: "worker",
          title: `Run: ${w.name}`,
          subtitle: `${w.model} · ${w.enabled ? "Active" : "Paused"}`,
          badge: {
            text: w.enabled ? "Run now" : "Paused",
            variant: w.enabled ? "brand" : "attention",
          },
          keywords: [w.name, w.model],
          perform: () => void handleTriggerWorker(w),
        })),
      },
    ];
  }, [workers, handleTriggerWorker]);

  // Active groups based on viewMode
  const activeGroups = useMemo(() => {
    if (viewMode === "run-worker") {
      return filterPaletteGroups(runWorkerGroups, search);
    }
    return filterPaletteGroups(rawGroups, search);
  }, [viewMode, runWorkerGroups, rawGroups, search]);

  const flattenedItems = useMemo(
    () => flattenGroupItems(activeGroups),
    [activeGroups]
  );

  return { activeGroups, flattenedItems };
}
